import { applyI18n, i18next, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { bindModalDismiss, createModalShell, refreshModalTitle } from './modalDialogShell';

export type InviteEmailLocale = 'en' | 'nl' | 'fr' | 'de';

const TEMPLATE_LOCALES: InviteEmailLocale[] = ['en', 'nl', 'fr', 'de'];

const LOCALE_LABELS: Record<InviteEmailLocale, string> = {
  en: 'English',
  nl: 'Nederlands',
  fr: 'Français',
  de: 'Deutsch',
};

const PLAIN_TEXT_INTERPOLATION = { escapeValue: false } as const;

export interface SedrGuestInviteEmailParams {
  guestName: string;
  guestEmail: string;
  roomTitle: string;
  inviteUrl: string;
  loginUrl: string;
  /** Called when the message was copied or opened in a mail client (counts as delivering the secret link). */
  onDelivered?: () => void;
}

export function buildSedrGuestInviteEmail(
  locale: InviteEmailLocale,
  params: SedrGuestInviteEmailParams,
): { subject: string; body: string } {
  const fixedT = i18next.getFixedT(locale);
  return {
    subject: String(
      fixedT('sedr.guestInvite.emailTemplate.subject', {
        roomTitle: params.roomTitle,
        interpolation: PLAIN_TEXT_INTERPOLATION,
      }),
    ),
    body: String(
      fixedT('sedr.guestInvite.emailTemplate.body', {
        guestName: params.guestName,
        roomTitle: params.roomTitle,
        inviteUrl: params.inviteUrl,
        loginUrl: params.loginUrl,
        interpolation: PLAIN_TEXT_INTERPOLATION,
      }),
    ),
  };
}

export function showSedrGuestInviteEmailDialog(params: SedrGuestInviteEmailParams): void {
  let draftLocale = (i18next.language?.slice(0, 2) as InviteEmailLocale) || 'en';
  if (!TEMPLATE_LOCALES.includes(draftLocale)) {
    draftLocale = 'en';
  }

  const shell = createModalShell({
    titleKey: 'sedr.guestInvite.emailTemplate.title',
    className: 'modal-dialog--invite-email',
  });

  const optionsHtml = TEMPLATE_LOCALES.map(
    (code) =>
      `<option value="${code}"${code === draftLocale ? ' selected' : ''}>${LOCALE_LABELS[code]}</option>`,
  ).join('');

  shell.body.innerHTML = `
    <form class="form-grid" id="sedr-invite-email-form">
      <p class="muted" data-i18n="sedr.guestInvite.emailTemplate.hint"></p>
      <label>
        <span data-i18n="sedr.guestInvite.emailTemplate.language"></span>
        <select id="sedr-invite-email-locale">${optionsHtml}</select>
      </label>
      <label>
        <span data-i18n="sedr.guestInvite.emailTemplate.subjectLabel"></span>
        <input type="text" id="sedr-invite-email-subject" readonly />
      </label>
      <label>
        <span data-i18n="sedr.guestInvite.emailTemplate.bodyLabel"></span>
        <textarea class="message-input sedr-invite-email-body" id="sedr-invite-email-body" rows="10" readonly></textarea>
      </label>
      <p class="muted" id="sedr-invite-email-status" hidden></p>
    </form>
  `;

  shell.actions.innerHTML = `
    <button type="button" class="button ghost" id="sedr-invite-email-close" data-i18n="info.owner.dialogClose"></button>
    <button type="button" class="button subtle" id="sedr-invite-email-mailto" data-i18n="sedr.guestInvite.emailTemplate.openMail"></button>
    <button type="button" class="button primary" id="sedr-invite-email-copy" data-i18n="sedr.guestInvite.emailTemplate.copy"></button>
  `;

  const localeSelect = shell.body.querySelector('#sedr-invite-email-locale') as HTMLSelectElement;
  const subjectInput = shell.body.querySelector('#sedr-invite-email-subject') as HTMLInputElement;
  const bodyInput = shell.body.querySelector('#sedr-invite-email-body') as HTMLTextAreaElement;
  const statusEl = shell.body.querySelector('#sedr-invite-email-status') as HTMLElement;

  const refreshTemplate = () => {
    const { subject, body } = buildSedrGuestInviteEmail(draftLocale, params);
    subjectInput.value = subject;
    bodyInput.value = body;
    statusEl.hidden = true;
    statusEl.textContent = '';
  };

  const refreshChrome = () => {
    applyI18n(shell.dialog);
    refreshModalTitle(shell, { titleKey: 'sedr.guestInvite.emailTemplate.title' });
    for (const option of Array.from(localeSelect.options)) {
      const code = option.value as InviteEmailLocale;
      option.textContent = LOCALE_LABELS[code] ?? code;
    }
    localeSelect.value = draftLocale;
    refreshTemplate();
  };

  localeSelect.addEventListener('change', () => {
    const value = localeSelect.value as InviteEmailLocale;
    if (!TEMPLATE_LOCALES.includes(value)) return;
    draftLocale = value;
    refreshTemplate();
  });

  const markDelivered = () => {
    params.onDelivered?.();
  };

  shell.actions.querySelector('#sedr-invite-email-close')?.addEventListener('click', () => close());

  shell.actions.querySelector('#sedr-invite-email-copy')?.addEventListener('click', () => {
    void (async () => {
      const text = `${subjectInput.value}\n\n${bodyInput.value}`;
      await navigator.clipboard.writeText(text);
      markDelivered();
      statusEl.hidden = false;
      statusEl.textContent = t('sedr.guestInvite.emailTemplate.copied');
    })();
  });

  shell.actions.querySelector('#sedr-invite-email-mailto')?.addEventListener('click', () => {
    // mailto: is plain-text only (RFC 6068). Bare http(s) URLs are auto-linked by most clients.
    const mailto = buildMailtoHref(params.guestEmail, subjectInput.value, bodyInput.value);
    if (!mailto) {
      statusEl.hidden = false;
      statusEl.textContent = t('sedr.guestInvite.emailTemplate.mailtoTooLong');
      return;
    }
    markDelivered();
    window.location.href = mailto;
  });

  const close = () => {
    unbindLocale();
    unbindDismiss();
    shell.backdrop.remove();
  };

  document.body.append(shell.backdrop);
  const unbindLocale = bindLocaleRefresh(refreshChrome);
  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });
  refreshChrome();
}

function buildMailtoHref(to: string, subject: string, body: string): string | null {
  const href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  if (href.length > 1800) {
    return null;
  }
  return href;
}
